<template>
  <div class="tournament-card">
    <div class="tournament-card__wrapper">
      <div :class="[type, item.status === TournamentStatus.COMPLETED ? 'completed' : '']" class="tournament-card__item" @click.prevent="showResults(item)">
        <div class="info">
          <div class="d-flex justify-content-between">
            <div class="name">{{ $t(`tournament_names.${type}`) }}</div>
            <div v-if="participant" class="participant-result">
              <span class="place">{{participant.place}}</span>
              <svg-icon icon="menu/tournaments"/>

            </div>
          </div>
          <div class="prize">
            <img src="/img/coins/gold_coin.svg">
            <span class="amount hidden">{{ numberFormatter.formatNumber(displayValue)?.replaceAll(/\d/g, '0') }}0</span>
            <span class="amount">{{ numberFormatter.formatNumber(displayValue)}}</span>
          </div>
          <div class="time">
            <img src="/img/timer.svg">
            <span class="duration" v-html="durationLabel"></span>
          </div>
          <div v-if="item.status === TournamentStatus.ACTIVE" class="controls">
            <button @click.stop="join(item)" type="button" class="btn btn-primary text-capitalize-first">
              {{ !item.isJoined ? $t('join') : $t('play') }}
            </button>
          </div>
          <div v-else-if="item.status === TournamentStatus.COMPLETED" class="controls">
            <button v-if="participant && !participant.isPrizeClaimed && !isZero(participant.prize) && !state.claimInProgress"
                    @click.stop="claim(participant)"
                    type="button"
                    class="btn btn-primary btn-claim text-capitalize-first">
              {{ $t('claim') }} <img src="/img/coins/gold_coin.svg"> <span>{{numberFormatter.formatNumber(participant.prize)}}</span>
            </button>
            <button v-else-if="state.claimInProgress" disabled class="btn btn-secondary btn-claim">
              <svg-icon icon="loader-icon"/> {{ $t('claiming')}}
            </button>
            <div v-else class="d-flex gap-sm justify-content-between">
              <button  @click.stop="showResults(item)" type="button" class="btn btn-secondary text-capitalize-first">
                {{ $t('results') }}
              </button>
              <div v-if="participant" class="won-results">
                {{$t('you_won')}}
                <img src="/img/coins/gold_coin.svg"> <span class="amount">{{numberFormatter.formatNumber(participant.prize)}}</span>
              </div>

            </div>
          </div>
        </div>
        <div class="icon">
          <img :src="`/img/tournaments/icons/${type}.png`">
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.btn-claim {
  i * {
    height: 16px;
    width: 16px;
  }
}
.won-results {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  gap: var(--margin-xxs);
  background: #000000b3;
  padding: 5px 10px;
  border-radius: 12px;
  right: 6px;
  top: 6px;

  img {
    height: 14px;
  }
  .amount {
    font-family: "Space Grotesk", sans-serif;
  }
}
.participant-result {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "Space Grotesk", sans-serif;
  background: #000000b3;
  padding: 5px 10px;
  border-radius: 12px;
  right: 16px;
  top: 16px;
  gap: var(--margin-xxs);
  font-weight: 700;

  i {
    line-height: 10px;
  }
  i * {
    fill: #FFFFFF;
    width: 20px;
    height: 20px;
  }

  .place {
    font-family: "Space Grotesk", sans-serif;
  }
}
.btn-claim {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--margin-xxs);
  img {
    height: 16px;
  }

  span {
    font-family: "Space Grotesk", sans-serif;
  }
}
</style>

<script setup lang="ts">
import TournamentDTO from "@/models/TournamentDTO";
import {computed, onMounted, ref, watch} from "vue";
import {tournamentName} from "@/models/enum/TournamentType";
import {numberFormatter} from "@/service/NumberFormatter";
import {endedLabelText, startInLabelText, timeLeftLabelText} from "@/service/DateHelper";
import {$t} from "@/i18n";
import {TournamentStatus} from "@/models/enum/TournamentStatus";
import {useStore} from "vuex";
import {AppEvents} from "@/models/enum/AppEvents";
import {useRouter} from "vue-router";
import TournamentParticipantDTO from "@/models/TournamentParticipantDTO";
import SvgIcon from "@/components/SvgIcon.vue";
import {isZero} from "@/models/MoneyDTO";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import {reactive} from "@vue/reactivity";

const props = defineProps<{
  item: TournamentDTO,
  participant?:TournamentParticipantDTO|null
}>()

const store = useStore()
const router = useRouter()
const state = reactive({
  claimInProgress: false as boolean
})
const notification = useNotification()
const displayValue = ref(props.item.currentBankroll.amount);
let from: number = 0
let to: number = 0
let start = performance.now();
let isAnimationInProgress = false

function animateValue(duration = 14000) {
  isAnimationInProgress = true

  function frame(now: number) {
    const progress = Math.min((now - start) / duration, 1);
    const value = from + (to - from) * progress;

    displayValue.value = Math.floor(value).toString()
    if (progress < 1) {
      requestAnimationFrame(frame);
    } else {
      isAnimationInProgress = false
    }
  }

  requestAnimationFrame(frame);
}

onMounted(() => {
  if (props.item.status === TournamentStatus.ACTIVE && props.item.startBankroll.amount !== props.item.currentBankroll.amount) {
    to = Number.parseFloat(props.item.currentBankroll.amount);
    from = to - 500;
    start = performance.now();

    animateValue();
  }
})

watch(() => props.item.currentBankroll.amount, (newVal, oldVal) => {
    to = Number.parseFloat(newVal);
    from = Number.parseFloat(oldVal);
    start = performance.now();

    if (to > from && !isAnimationInProgress) {
      animateValue();
    } else {
      displayValue.value = newVal;
    }
  }
);

const claim = async (participant: TournamentParticipantDTO) => {
  try {
    state.claimInProgress = true
    await api.call.tournaments.claim(participant.tournamentId)
    store.dispatch('updateUser')
    const text = $t('amount_added_to_balance', {amount: numberFormatter.formatNumber(participant.prize)});
    notification.notify({type: 'success', title: `<div class="d-flex align-items-center gap-1"><img src="/img/coins/gold_coin.svg"> ${text}</div>`, duration: 1500})
    participant.isPrizeClaimed = true
  } catch (e) {

  } finally {
    state.claimInProgress = false
  }
}

const showResults = async (item: TournamentDTO) => {
  router.push({name: 'tournamentInfo', params: {id: item.id}, })
}

const join = async (item: TournamentDTO) => {
  if (!store.getters.isLoggedIn) {
    router.push({name: 'login'})
    return;
  }
  store.dispatch('joinTournament', item.id)
  store.dispatch('emit', {event: AppEvents.SHOW_SELECT_GAME_TO_PLAY})
}

const type = computed(() => {
  return tournamentName(props.item.type)
})

const durationLabel = computed(() => {
  switch (props.item.status) {
    case TournamentStatus.ACTIVE: return timeLeftLabelText(props.item.endAt)
    case TournamentStatus.ANNOUNCEMENT: return startInLabelText(props.item.startAt)
    case TournamentStatus.COMPLETED: return endedLabelText(props.item.endAt)
  }
})

</script>